import React from "react";
import { Num } from "./ui/Num";

/**
 * The one-sentence cross-channel summary that sits under the two funnels.
 *
 * ---------------------------------------------------------------------------
 * NOTHING IS COMPUTED HERE. EVERY FIGURE COMES FROM THE SERVER.
 * ---------------------------------------------------------------------------
 * `totalSends` counts MESSAGES. `uniqueReached` counts PEOPLE. A user who is in
 * both the push audience and the email audience is two sends and one person,
 * and only the server knows the size of that overlap.
 *
 * Adding the two channel totals to report reach would therefore claim to have
 * reached more people than exist — and it would do it in the single sentence
 * most likely to be quoted out of the console into a slide. That is why this
 * component takes a PRE-COMPUTED object and does no arithmetic: there is no
 * `+`, no `-` and no division anywhere in this file, and there must never be
 * one. If a new combined figure is wanted, it arrives as its own server field.
 *
 * `reachedOnBothChannels` is the server's own overlap count, which is the only
 * honest way to say how many people got the message twice.
 * ---------------------------------------------------------------------------
 *
 * Purely presentational: nothing here fetches, and nothing imports a service.
 */

/**
 * Does the overlap clause earn its place in the sentence?
 *
 * `null` means the server did not say and `0` means nobody was in both
 * audiences — in neither case is "0 got both a push and an email" worth a
 * clause. Only a real, positive overlap is news.
 *
 * @param {unknown} both
 * @returns {boolean}
 */
const hasOverlap = (both) =>
  typeof both === "number" && Number.isFinite(both) && both > 0;

/**
 * The combined-reach sentence.
 *
 * Deliberately does NOT mention openers: opens are inflated by mailbox
 * providers prefetching the tracking pixel, and a plain-prose sentence has
 * nowhere to hang the caveat that number needs. Sends, reach and overlap are
 * all facts, so all three can be stated flatly.
 *
 * @param {Object} props
 * @param {Object|null} props.combined - Decoded combined block: `{uniqueRecipients, uniqueReached, uniqueOpeners, uniqueClickers, reachedOnBothChannels, totalSends}`. Any field may be null; the whole object may be null.
 * @param {string} [props.className] - Extra classes, merged last.
 * @returns {React.ReactElement|null}
 */
export const CombinedLine = ({ combined, className = "" }) => {
  // No combined block means the server had nothing to say across channels.
  // A sentence made of em dashes would say less than no sentence at all.
  if (!combined) return null;

  const { totalSends, uniqueReached, reachedOnBothChannels } = combined;

  return (
    <p
      data-testid="bc-combined-line"
      className={`text-sm text-bc-text-2 ${className}`}
    >
      Combined <Num value={totalSends} emphasis /> sends. Reached{" "}
      <Num value={uniqueReached} emphasis /> unique people
      {hasOverlap(reachedOnBothChannels) ? (
        <>
          {" — "}
          <Num value={reachedOnBothChannels} emphasis /> got both a push and an
          email
        </>
      ) : null}
      .
    </p>
  );
};

export default CombinedLine;
